import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewRef, signal } from '@angular/core';
import { FieldType } from '@ngx-formly/core';
import { Options } from 'ngx-slider-v2';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AppStateService } from 'src/app/core-module/services/app-state.service';
import { AloAocStateService } from '../../../core-module/services/state/alo-aoc-state.service';
import { CheckboxStateService } from '../../../core-module/services/state/checkbox-state.service';
import { FeatureDefaultsStateService } from '../../../core-module/services/state/feature-defaults-state.service';
import { FeatureFlagsStateService } from '../../../core-module/services/state/feature-flags-state.service';
import { InputErrorsStateService } from '../../../core-module/services/state/input-errors-state.service';

@Component({
  standalone: false,
  selector: 'app-dsc',
  templateUrl: './dsc.component.html',
  styleUrls: ['./dsc.component.scss']
})
export class DscComponent extends FieldType implements OnInit, OnDestroy {

  // Feature enable state
  isEnableFeature: boolean = false;
  private readonly dscDisabledSignal = signal(false);
  showEnableFeature = false;

  // DSC parameter values (initialized from schema in loadFromSchema())
  enabled: boolean = false;
  
  presenceLevel: number = 0;
  presenceLevelDisplay: number | string = '';
  presenceLevelUseDynadimmer = false;
  presenceLevelHasDynadimmer = false;
  presenceLevelDynadimmerValue = 0;
  presenceFadeTime: number = 0;
  presenceFadeTimeDisplay: number | string = '';
  presenceHoldTime: number = 0;
  presenceHoldTimeDisplay: number | string = '';
  presenceHoldTimeInfinite = false;

  noPresenceLevel: number = 0;
  noPresenceLevelDisplay: number | string = '';
  noPresenceLevelUseDynadimmer = false;
  noPresenceLevelHasDynadimmer = false;
  noPresenceLevelDynadimmerValue = 0;
  noPresenceFadeTime: number = 0;
  noPresenceFadeTimeDisplay: number | string = '';
  noPresenceProlongTime: number = 0;
  noPresenceProlongTimeDisplay: number | string = '';
  noPresenceProlongTimeInfinite = false;

  sunsetThreshold: number = 0;
  sunsetThresholdDisplay: number | string = '';
  sunsetThresholdHasOneOf = false;
  sunriseThreshold: number = 0;
  sunriseThresholdDisplay: number | string = '';
  sunriseThresholdHasOneOf = false;
  letLightSensorControl = false;
  
  // Slider options (initialized from schema in loadFromSchema())
  presenceLevelOptions: Options = { floor: 0, ceil: 0, showSelectionBar: true };
  presenceFadeTimeOptions: Options = { floor: 0, ceil: 0, showSelectionBar: true };
  presenceHoldTimeOptions: Options = { floor: 0, ceil: 0, showSelectionBar: true };
  noPresenceLevelOptions: Options = { floor: 0, ceil: 0, showSelectionBar: true };
  noPresenceFadeTimeOptions: Options = { floor: 0, ceil: 0, showSelectionBar: true };
  noPresenceProlongTimeOptions: Options = { floor: 0, ceil: 0, showSelectionBar: true };
  sunsetThresholdOptions: Options = { floor: 0, ceil: 0, showSelectionBar: true };
  sunriseThresholdOptions: Options = { floor: 0, ceil: 0, showSelectionBar: true };

  // Min/max constraints (loaded from schema in loadFromSchema())
  presenceLevelMin = 0;
  presenceLevelMax = 0;
  presenceFadeTimeMin = 0;
  presenceFadeTimeMax = 0;
  presenceHoldTimeMin = 0;
  presenceHoldTimeMax = 0;
  presenceHoldTimeInfiniteValue = 0;

  noPresenceLevelMin = 0;
  noPresenceLevelMax = 0;
  noPresenceFadeTimeMin = 0;
  noPresenceFadeTimeMax = 0;
  noPresenceProlongTimeMin = 0;
  noPresenceProlongTimeMax = 0;
  noPresenceProlongTimeInfiniteValue = 0;

  sunsetThresholdMin = 0;
  sunsetThresholdMax = 0;
  sunsetThresholdLightSensorValue = 0;
  sunriseThresholdMin = 0;
  sunriseThresholdMax = 0;
  sunriseThresholdLightSensorValue = 0;

  // Schema-derived defaults (loaded from schema, not hardcoded)
  private isEnableFeatureDefault: boolean;
  private enabledDefault = false;
  private presenceLevelDefault = 0;
  private presenceLevelUseDynadimmerDefault = false;
  private presenceLevelDynadimmerValueDefault = 0;
  private presenceFadeTimeDefault = 0;
  private presenceHoldTimeDefault = 0;
  private presenceHoldTimeInfiniteDefault = false;  // Always unchecked by default

  private noPresenceLevelDefault = 0;
  private noPresenceLevelUseDynadimmerDefault = false;
  private noPresenceLevelDynadimmerValueDefault = 0;
  private noPresenceFadeTimeDefault = 0;
  private noPresenceProlongTimeDefault = 0;
  private noPresenceProlongTimeInfiniteDefault = false;  // Always unchecked by default

  private sunsetThresholdDefault = 0;
  private sunriseThresholdDefault = 0;
  private letLightSensorControlDefault = false;  // Always unchecked by default

  // Include-in-configuration checkbox state
  isIncludedInConfiguration = false;
  private ignoreParentUpdate = false;
  private hasConfigData = false;

  // Error tracking
  inputError: { [key: string]: boolean } = {};
  hasInputError = false;
  dscDisabledButton = false;

  // Standard/Advanced mode
  quickConfigvalue: boolean;
  quickConfigCustomCSS: string;
  setQuickConfig: string;

  // Default-color tracking (drives Restore Default button)
  defaultColor = true;

  private isResetting = false;
  private isBlurring = false;
  private properties: any;

  // Input debouncing subjects
  private readonly presenceLevelInput$ = new Subject<string>();
  private readonly presenceFadeTimeInput$ = new Subject<string>();
  private readonly presenceHoldTimeInput$ = new Subject<string>();
  private readonly noPresenceLevelInput$ = new Subject<string>();
  private readonly noPresenceFadeTimeInput$ = new Subject<string>();
  private readonly noPresenceProlongTimeInput$ = new Subject<string>();
  private readonly sunsetThresholdInput$ = new Subject<string>();
  private readonly sunriseThresholdInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly ref: ChangeDetectorRef,
    private readonly appStateService: AppStateService,
    private readonly featureDefaults: FeatureDefaultsStateService,
    private readonly featureFlags: FeatureFlagsStateService,
    private readonly checkboxState: CheckboxStateService,
    private readonly inputErrors: InputErrorsStateService,
    private readonly aloAoc: AloAocStateService
  ) {
    super();
  }

  get dscDisabled(): boolean { return this.dscDisabledSignal(); }
  set dscDisabled(value: boolean) { this.dscDisabledSignal.set(!!value); }

  // Check if device has Dynadimmer capability (required for sunset/sunrise threshold visibility)
  get hasDynadimmer(): boolean {
    return this.presenceLevelHasDynadimmer || this.noPresenceLevelHasDynadimmer;
  }

  ngOnInit(): void {
    // Protect initial load from parent checkbox updates
    this.ignoreParentUpdate = true;

    this.properties = (this.field as any)?.properties;

    // Check if Enabled exists in required properties
    const column = (this.field as any).requiredProperties;
    this.showEnableFeature = column?.includes('Enabled') ?? false;

    if (this.properties) {
      this.loadFromSchema();
    }

    this.setupInputDebouncing();

    // Load saved configuration data (matching OTP pattern)
    let savedValue: any = null;
    let isConfigEdited = false;

    // Check UnSelectedFeatConfig first (for edited but unselected features)
    const unSelectedFeat = this.appStateService.getUnSelectedFeatConfig();
    if (unSelectedFeat != null && Array.isArray(unSelectedFeat)) {
      let featIndex = 0;
      unSelectedFeat.forEach((element) => {
        if (element['featureName'] && element['featureName'].toString().toUpperCase() === 'DSC') {
          isConfigEdited = true;
          savedValue = element['featureParameters'][0];
          unSelectedFeat.splice(featIndex, 1);
          this.appStateService.setUnSelectedFeatConfig(unSelectedFeat);
        } else {
          featIndex++;
        }
      });
    }

    // Use centralized method to get feature data
    if (!isConfigEdited) {
      const saveReadValue = this.appStateService.getSaveReadValue();
      if (saveReadValue?.DSC && Object.keys(saveReadValue.DSC).length > 0) {
        savedValue = saveReadValue.DSC;
        this.hasConfigData = true;
        delete saveReadValue.DSC;
        this.appStateService.setSaveReadValue(Object.keys(saveReadValue).length > 0 ? saveReadValue : null);
      } else {
        const dscData = this.appStateService.getFeatureData('DSC');
        if (dscData) {
          savedValue = dscData;
          this.hasConfigData = Object.keys(savedValue).length > 0;
        }
      }
    }

    // Load values from saved data
    if (savedValue && Object.keys(savedValue).length > 0) {
      this.loadDataFromObject(savedValue);
    }

    // Set initial disabled state based on enable feature checkbox
    if (this.showEnableFeature) {
      // Start with everything disabled, only enable when checkbox is checked
      this.dscDisabled = !this.isEnableFeature;
      // Initialize all slider options with disabled state (also consider dynadimmer/infinite checkboxes)
      this.presenceLevelOptions = Object.assign({}, this.presenceLevelOptions, { disabled: this.dscDisabled || this.presenceLevelUseDynadimmer });
      this.presenceFadeTimeOptions = Object.assign({}, this.presenceFadeTimeOptions, { disabled: this.dscDisabled });
      this.presenceHoldTimeOptions = Object.assign({}, this.presenceHoldTimeOptions, { disabled: this.dscDisabled || this.presenceHoldTimeInfinite });
      this.noPresenceLevelOptions = Object.assign({}, this.noPresenceLevelOptions, { disabled: this.dscDisabled || this.noPresenceLevelUseDynadimmer });
      this.noPresenceFadeTimeOptions = Object.assign({}, this.noPresenceFadeTimeOptions, { disabled: this.dscDisabled });
      this.noPresenceProlongTimeOptions = Object.assign({}, this.noPresenceProlongTimeOptions, { disabled: this.dscDisabled || this.noPresenceProlongTimeInfinite });
      // Sunrise/Sunset sliders should always be editable (only controlled by letLightSensorControl checkbox)
      // This allows users to fix out-of-range values even when Enable feature checkbox is unchecked
      this.sunsetThresholdOptions = Object.assign({}, this.sunsetThresholdOptions, { disabled: !this.letLightSensorControl });
      this.sunriseThresholdOptions = Object.assign({}, this.sunriseThresholdOptions, { disabled: !this.letLightSensorControl });
    } else {
      // If enable feature checkbox is not shown, ensure fields are not disabled (but respect other checkboxes)
      this.dscDisabled = false;
      this.presenceLevelOptions = Object.assign({}, this.presenceLevelOptions, { disabled: this.presenceLevelUseDynadimmer });
      this.presenceHoldTimeOptions = Object.assign({}, this.presenceHoldTimeOptions, { disabled: this.presenceHoldTimeInfinite });
      this.noPresenceLevelOptions = Object.assign({}, this.noPresenceLevelOptions, { disabled: this.noPresenceLevelUseDynadimmer });
      this.noPresenceProlongTimeOptions = Object.assign({}, this.noPresenceProlongTimeOptions, { disabled: this.noPresenceProlongTimeInfinite });
      // For threshold sliders, respect letLightSensorControl state
      this.sunsetThresholdOptions = Object.assign({}, this.sunsetThresholdOptions, { disabled: !this.letLightSensorControl });
      this.sunriseThresholdOptions = Object.assign({}, this.sunriseThresholdOptions, { disabled: !this.letLightSensorControl });
    }

    // Validate all fields first to detect any invalid loaded values
    this.revalidateAllFields();
    this.recalcErrorState();
    
    // Only update form control if there are no validation errors
    if (!this.dscDisabledButton) {
      this.updateFormControl();
    }
    
    this.featureDefaults.setDscDefaultColor(true);

    this.featureDefaults.getDscDefaultValue
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response === 'valid') {
          this.resetToDefaults();
          this.featureDefaults.setDscDefaultValue('');
        }
      });

    this.checkboxState.getDscParentCheckbox
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        if (state !== null && !this.ignoreParentUpdate) {
          this.isIncludedInConfiguration = state.checked;

          if (!state.checked) {
            this.inputErrors.setDscInputError.next(false);
            this.ref.detectChanges();
            return;
          }

          if (this.hasConfigData) {
            this.sendErrorStateToParent();
            this.ref.detectChanges();
            return;
          }

          this.updateFormControl();
          this.sendErrorStateToParent();
          this.ref.detectChanges();
        }
      });

    // Already validated above, but call checkDefaults
    this.checkDefaults();

    // Enable checkbox if any value is non-default
    if (!this.defaultColor) {
      this.checkboxState.enableDscCheckbox();
      this.isIncludedInConfiguration = true;
      this.hasConfigData = true;
    }

    this.sendErrorStateToParent();

    // Subscribe to Standard/Advanced mode changes
    this.aloAoc.getquickConfig
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        if (data === 'quickConfig') {
          this.quickConfigvalue = true;
          this.quickConfigCustomCSS = 'quick-custom-inputbox textBoxUnit';
          this.setQuickConfig = 'quickConfig';
        } else if (data === 'standard') {
          this.quickConfigvalue = false;
          this.quickConfigCustomCSS = 'custom-inputbox';
          this.setQuickConfig = 'standard';
        }
        setTimeout(() => {
          if (this.ref && !(this.ref as ViewRef).destroyed) {
            this.ref.detectChanges();
          }
        });
      });

    this.featureFlags.getDscFeatApi
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: any) => {
        if (!data) { return; }

        // Check if data is just a boolean flag (true/false) instead of actual data object
        if (typeof data === 'boolean') {
          const dscData = this.appStateService.getFeatureData('DSC');
          if (dscData && Object.keys(dscData).length > 0) {
            this.loadDataFromObject(dscData);
            this.hasConfigData = true;
            this.checkDefaults();
            this.updateFormControl();
            this.revalidateAllFields();
            this.sendErrorStateToParent();
            this.ref.detectChanges();
          }
          return;
        }

        if (this.hasConfigData) {
          // User already has data — preserve current state (including any errors)
          this.sendErrorStateToParent();
          return;
        }

        if (data.PresenceLevel !== undefined) {
          const level = this.getNumber(data.PresenceLevel, this.presenceLevel);
          if (level === this.presenceLevelDynadimmerValue) {
            this.presenceLevelUseDynadimmer = true;
            this.presenceLevel = this.presenceLevelDynadimmerValue;
            this.presenceLevelDisplay = '';
          } else {
            this.presenceLevelUseDynadimmer = false;
            this.presenceLevel = level;
            // Validate before displaying
            const isValid = level >= this.presenceLevelMin && level <= this.presenceLevelMax;
            this.presenceLevelDisplay = isValid ? level : '';
          }
        }
        if (data.PresenceFadeTime !== undefined) {
          const fadeTime = this.getNumber(data.PresenceFadeTime, this.presenceFadeTime);
          this.presenceFadeTime = fadeTime;
          // Validate before displaying
          const isValid = fadeTime >= this.presenceFadeTimeMin && fadeTime <= this.presenceFadeTimeMax;
          this.presenceFadeTimeDisplay = isValid ? fadeTime : '';
        }
        if (data.PresenceHoldTime !== undefined) {
          const holdTime = this.getNumber(data.PresenceHoldTime, this.presenceHoldTime);
          if (holdTime === this.presenceHoldTimeInfiniteValue) {
            this.presenceHoldTimeInfinite = true;
            this.presenceHoldTime = this.presenceHoldTimeInfiniteValue;
            this.presenceHoldTimeDisplay = '';
          } else {
            this.presenceHoldTimeInfinite = false;
            this.presenceHoldTime = holdTime;
            // Validate before displaying
            const isValid = holdTime >= this.presenceHoldTimeMin && holdTime <= this.presenceHoldTimeMax;
            this.presenceHoldTimeDisplay = isValid ? holdTime : '';
          }
        }
        if (data.NoPresenceLevel !== undefined) {
          const level = this.getNumber(data.NoPresenceLevel, this.noPresenceLevel);
          if (level === this.noPresenceLevelDynadimmerValue) {
            this.noPresenceLevelUseDynadimmer = true;
            this.noPresenceLevel = this.noPresenceLevelDynadimmerValue;
            this.noPresenceLevelDisplay = '';
          } else {
            this.noPresenceLevelUseDynadimmer = false;
            this.noPresenceLevel = level;
            // Validate before displaying
            const isValid = level >= this.noPresenceLevelMin && level <= this.noPresenceLevelMax;
            this.noPresenceLevelDisplay = isValid ? level : '';
          }
        }
        if (data.NoPresenceFadeTime !== undefined) {
          const fadeTime = this.getNumber(data.NoPresenceFadeTime, this.noPresenceFadeTime);
          this.noPresenceFadeTime = fadeTime;
          // Validate before displaying
          const isValid = fadeTime >= this.noPresenceFadeTimeMin && fadeTime <= this.noPresenceFadeTimeMax;
          this.noPresenceFadeTimeDisplay = isValid ? fadeTime : '';
        }
        if (data.NoPresenceProlongTime !== undefined) {
          const prolongTime = this.getNumber(data.NoPresenceProlongTime, this.noPresenceProlongTime);
          if (prolongTime === this.noPresenceProlongTimeInfiniteValue) {
            this.noPresenceProlongTimeInfinite = true;
            this.noPresenceProlongTime = this.noPresenceProlongTimeInfiniteValue;
            this.noPresenceProlongTimeDisplay = '';
          } else {
            this.noPresenceProlongTimeInfinite = false;
            this.noPresenceProlongTime = prolongTime;
            // Validate before displaying
            const isValid = prolongTime >= this.noPresenceProlongTimeMin && prolongTime <= this.noPresenceProlongTimeMax;
            this.noPresenceProlongTimeDisplay = isValid ? prolongTime : '';
          }
        }
        if (data.Enabled !== undefined) {
          this.enabled = Boolean(data.Enabled);
        }
        
        // Track if threshold values were null (cleared by user)
        const sunsetWasNull = data.SunsetThreshold === null || data.SunsetThreshold === undefined;
        const sunriseWasNull = data.SunriseThreshold === null || data.SunriseThreshold === undefined;
        
        
        if (!sunsetWasNull) {
          const threshold = this.getNumber(data.SunsetThreshold, this.sunsetThreshold);
          this.sunsetThreshold = threshold;
        }
        if (!sunriseWasNull) {
          const threshold = this.getNumber(data.SunriseThreshold, this.sunriseThreshold);
          this.sunriseThreshold = threshold;
        }

        // Derive letLightSensorControl state from threshold values and validate display
        if (this.sunsetThreshold === this.sunsetThresholdLightSensorValue && 
            this.sunriseThreshold === this.sunriseThresholdLightSensorValue) {
          this.letLightSensorControl = false;
          this.sunsetThresholdDisplay = '';
          this.sunriseThresholdDisplay = '';
        } else {
          this.letLightSensorControl = true;
          // Only display if NOT null (respect cleared state)
          if (!sunsetWasNull) {
            const sunsetValid = this.sunsetThreshold >= this.sunsetThresholdMin && this.sunsetThreshold <= this.sunsetThresholdMax;
            this.sunsetThresholdDisplay = sunsetValid ? this.sunsetThreshold : '';
          }
          if (!sunriseWasNull) {
            const sunriseValid = this.sunriseThreshold >= this.sunriseThresholdMin && this.sunriseThreshold <= this.sunriseThresholdMax;
            this.sunriseThresholdDisplay = sunriseValid ? this.sunriseThreshold : '';
          }
        }

        this.checkDefaults();
        this.updateFormControl();
        this.revalidateAllFields();
        this.hasConfigData = true;
        this.sendErrorStateToParent();
        this.ref.detectChanges();
      });

    this.ignoreParentUpdate = false;
  }

  private loadDataFromObject(data: any): void {
    // Handle PresenceLevel - if null or undefined, keep empty display (persists cleared state)
    if (data.PresenceLevel === null || data.PresenceLevel === undefined) {
      this.presenceLevelDisplay = '';
      // Keep default internal value for validation
    } else {
      this.presenceLevel = data.PresenceLevel;
      if (data.PresenceLevelUseDynadimmer !== undefined) {
        this.presenceLevelUseDynadimmer = data.PresenceLevelUseDynadimmer;
      } else {
        this.presenceLevelUseDynadimmer = (data.PresenceLevel === this.presenceLevelDynadimmerValue);
      }
      if (this.presenceLevelUseDynadimmer) {
        this.presenceLevelDisplay = '';
      } else {
        // Restore display value exactly as saved (preserves invalid/error-state values)
        this.presenceLevelDisplay = data.PresenceLevelDisplay !== undefined ? data.PresenceLevelDisplay : 
                                    (data.PresenceLevel !== null && data.PresenceLevel !== undefined ? data.PresenceLevel : '');
      }
    }
    // Handle PresenceFadeTime - if null, keep empty display
    if (data.PresenceFadeTime === null || data.PresenceFadeTime === undefined) {
      this.presenceFadeTimeDisplay = '';
    } else {
      this.presenceFadeTime = data.PresenceFadeTime;
      // Restore display value exactly as saved (preserves invalid/error-state values)
      this.presenceFadeTimeDisplay = data.PresenceFadeTimeDisplay !== undefined ? data.PresenceFadeTimeDisplay : 
                                     (data.PresenceFadeTime !== null && data.PresenceFadeTime !== undefined ? data.PresenceFadeTime : '');
    }
    // Handle PresenceHoldTime - if null, keep empty display
    if (data.PresenceHoldTime === null || data.PresenceHoldTime === undefined) {
      this.presenceHoldTimeDisplay = '';
    } else {
      this.presenceHoldTime = data.PresenceHoldTime;
      if (data.PresenceHoldTimeInfinite !== undefined) {
        this.presenceHoldTimeInfinite = data.PresenceHoldTimeInfinite;
      } else {
        this.presenceHoldTimeInfinite = (data.PresenceHoldTime === this.presenceHoldTimeInfiniteValue);
      }
      if (this.presenceHoldTimeInfinite) {
        this.presenceHoldTimeDisplay = '';
      } else {
        // Restore display value exactly as saved (preserves invalid/error-state values)
        this.presenceHoldTimeDisplay = data.PresenceHoldTimeDisplay !== undefined ? data.PresenceHoldTimeDisplay : 
                                       (data.PresenceHoldTime !== null && data.PresenceHoldTime !== undefined ? data.PresenceHoldTime : '');
      }
    }
    // Handle NoPresenceLevel - if null, keep empty display
    if (data.NoPresenceLevel === null || data.NoPresenceLevel === undefined) {
      this.noPresenceLevelDisplay = '';
    } else {
      this.noPresenceLevel = data.NoPresenceLevel;
      if (data.NoPresenceLevelUseDynadimmer !== undefined) {
        this.noPresenceLevelUseDynadimmer = data.NoPresenceLevelUseDynadimmer;
      } else {
        this.noPresenceLevelUseDynadimmer = (data.NoPresenceLevel === this.noPresenceLevelDynadimmerValue);
      }
      if (this.noPresenceLevelUseDynadimmer) {
        this.noPresenceLevelDisplay = '';
      } else {
        // Restore display value exactly as saved (preserves invalid/error-state values)
        this.noPresenceLevelDisplay = data.NoPresenceLevelDisplay !== undefined ? data.NoPresenceLevelDisplay : 
                                      (data.NoPresenceLevel !== null && data.NoPresenceLevel !== undefined ? data.NoPresenceLevel : '');
      }
    }
    // Handle NoPresenceFadeTime - if null, keep empty display
    if (data.NoPresenceFadeTime === null || data.NoPresenceFadeTime === undefined) {
      this.noPresenceFadeTimeDisplay = '';
    } else {
      this.noPresenceFadeTime = data.NoPresenceFadeTime;
      // Restore display value exactly as saved (preserves invalid/error-state values)
      this.noPresenceFadeTimeDisplay = data.NoPresenceFadeTimeDisplay !== undefined ? data.NoPresenceFadeTimeDisplay : 
                                       (data.NoPresenceFadeTime !== null && data.NoPresenceFadeTime !== undefined ? data.NoPresenceFadeTime : '');
    }
    // Handle NoPresenceProlongTime - if null, keep empty display
    if (data.NoPresenceProlongTime === null || data.NoPresenceProlongTime === undefined) {
      this.noPresenceProlongTimeDisplay = '';
    } else {
      this.noPresenceProlongTime = data.NoPresenceProlongTime;
      if (data.NoPresenceProlongTimeInfinite !== undefined) {
        this.noPresenceProlongTimeInfinite = data.NoPresenceProlongTimeInfinite;
      } else {
        this.noPresenceProlongTimeInfinite = (data.NoPresenceProlongTime === this.noPresenceProlongTimeInfiniteValue);
      }
      if (this.noPresenceProlongTimeInfinite) {
        this.noPresenceProlongTimeDisplay = '';
      } else {
        // Restore display value exactly as saved (preserves invalid/error-state values)
        this.noPresenceProlongTimeDisplay = data.NoPresenceProlongTimeDisplay !== undefined ? data.NoPresenceProlongTimeDisplay : 
                                            (data.NoPresenceProlongTime !== null && data.NoPresenceProlongTime !== undefined ? data.NoPresenceProlongTime : '');
      }
    }
    if (data.Enabled !== undefined) {
      this.enabled = data.Enabled;
      this.isEnableFeature = data.Enabled;
    }
    
    // Track if threshold values were explicitly null (cleared by user)
    const sunsetWasNull = data.SunsetThreshold === null || data.SunsetThreshold === undefined;
    const sunriseWasNull = data.SunriseThreshold === null || data.SunriseThreshold === undefined;
    
    // Handle SunsetThreshold - if null, keep empty display
    if (sunsetWasNull) {
      this.sunsetThresholdDisplay = '';
    } else {
      this.sunsetThreshold = data.SunsetThreshold;
    }
    // Handle SunriseThreshold - if null, keep empty display
    if (sunriseWasNull) {
      this.sunriseThresholdDisplay = '';
    } else {
      this.sunriseThreshold = data.SunriseThreshold;
    }

    if (data.LetLightSensorControl !== undefined) {
      this.letLightSensorControl = data.LetLightSensorControl;
      if (!this.letLightSensorControl) {
        // When checkbox is unchecked, display should be empty
        this.sunsetThresholdDisplay = '';
        this.sunriseThresholdDisplay = '';
      } else {
        // When checkbox is checked, restore display values exactly as saved
        if (!sunsetWasNull) {
          this.sunsetThresholdDisplay = data.SunsetThresholdDisplay !== undefined ? data.SunsetThresholdDisplay : 
                                        (data.SunsetThreshold !== null && data.SunsetThreshold !== undefined ? data.SunsetThreshold : '');
        }
        if (!sunriseWasNull) {
          this.sunriseThresholdDisplay = data.SunriseThresholdDisplay !== undefined ? data.SunriseThresholdDisplay : 
                                         (data.SunriseThreshold !== null && data.SunriseThreshold !== undefined ? data.SunriseThreshold : '');
        }
      }
    } else {
      if (this.sunsetThreshold === this.sunsetThresholdLightSensorValue && 
          this.sunriseThreshold === this.sunriseThresholdLightSensorValue) {
        this.letLightSensorControl = false;
        this.sunsetThresholdDisplay = '';
        this.sunriseThresholdDisplay = '';
      } else {
        this.letLightSensorControl = true;
        // Restore display values exactly as saved
        if (!sunsetWasNull) {
          this.sunsetThresholdDisplay = data.SunsetThresholdDisplay !== undefined ? data.SunsetThresholdDisplay : 
                                        (data.SunsetThreshold !== null && data.SunsetThreshold !== undefined ? data.SunsetThreshold : '');
        }
        if (!sunriseWasNull) {
          this.sunriseThresholdDisplay = data.SunriseThresholdDisplay !== undefined ? data.SunriseThresholdDisplay : 
                                         (data.SunriseThreshold !== null && data.SunriseThreshold !== undefined ? data.SunriseThreshold : '');
        }
      }
    }
  }

  private revalidateAllFields(): void {
    // Validate all numeric fields are within their respective ranges
    // Always validate presenceLevel (accepts both range and dynadimmer const value)
    this.validateFieldWithConstValue('presenceLevel', this.presenceLevel, this.presenceLevelMin, this.presenceLevelMax, this.presenceLevelDynadimmerValue);
    this.validateField('presenceFadeTime', this.presenceFadeTime, this.presenceFadeTimeMin, this.presenceFadeTimeMax);
    // Always validate presenceHoldTime (accepts both range and infinite const value)
    this.validateFieldWithConstValue('presenceHoldTime', this.presenceHoldTime, this.presenceHoldTimeMin, this.presenceHoldTimeMax, this.presenceHoldTimeInfiniteValue);
    // Always validate noPresenceLevel (accepts both range and dynadimmer const value)
    this.validateFieldWithConstValue('noPresenceLevel', this.noPresenceLevel, this.noPresenceLevelMin, this.noPresenceLevelMax, this.noPresenceLevelDynadimmerValue);
    this.validateField('noPresenceFadeTime', this.noPresenceFadeTime, this.noPresenceFadeTimeMin, this.noPresenceFadeTimeMax);
    // Always validate noPresenceProlongTime (accepts both range and infinite const value)
    this.validateFieldWithConstValue('noPresenceProlongTime', this.noPresenceProlongTime, this.noPresenceProlongTimeMin, this.noPresenceProlongTimeMax, this.noPresenceProlongTimeInfiniteValue);
    // Validate thresholds: only accept const (0) when checkbox is unchecked, otherwise only accept range
    this.validateThresholdField('sunsetThreshold', this.sunsetThreshold, this.sunsetThresholdMin, this.sunsetThresholdMax, this.sunsetThresholdLightSensorValue);
    this.validateThresholdField('sunriseThreshold', this.sunriseThreshold, this.sunriseThresholdMin, this.sunriseThresholdMax, this.sunriseThresholdLightSensorValue);
    this.recalcErrorState();
  }

  private validateThresholdField(fieldName: string, value: number, min: number, max: number, constValue: number): void {
    if (value === null || value === undefined || isNaN(value as any)) {
      this.inputError[fieldName] = true;
      return;
    }
    
    // If checkbox is unchecked and value equals const, it's valid
    if (!this.letLightSensorControl && value === constValue) {
      this.inputError[fieldName] = false;
      return;
    }
    
    // If const-only field (min == max == const), value must equal const
    if (min === max && min === constValue) {
      this.inputError[fieldName] = value !== constValue;
      return;
    }
    
    const displayValue = this.getDisplayValue(fieldName);
    if (displayValue === '') {
      this.inputError[fieldName] = true;
      return;
    }
    
    const isInRange = value >= min && value <= max;
    this.inputError[fieldName] = !isInRange;
  }

  private validateField(fieldName: string, value: number, min: number, max: number): void {
    if (value === null || value === undefined || isNaN(value as any)) {
      this.inputError[fieldName] = true;
      return;
    }
    
    const displayValue = this.getDisplayValue(fieldName);
    if (displayValue === '') {
      this.inputError[fieldName] = true;
      return;
    }
    
    this.inputError[fieldName] = !(value >= min && value <= max);
  }

  private validateFieldWithConstValue(fieldName: string, value: number, min: number, max: number, constValue: number): void {
    if (value === null || value === undefined || isNaN(value as any)) {
      this.inputError[fieldName] = true;
      return;
    }
    
    let isCheckboxChecked = false;
    if (fieldName === 'presenceLevel') {
      isCheckboxChecked = this.presenceLevelUseDynadimmer;
    } else if (fieldName === 'noPresenceLevel') {
      isCheckboxChecked = this.noPresenceLevelUseDynadimmer;
    } else if (fieldName === 'presenceHoldTime') {
      isCheckboxChecked = this.presenceHoldTimeInfinite;
    } else if (fieldName === 'noPresenceProlongTime') {
      isCheckboxChecked = this.noPresenceProlongTimeInfinite;
    }
    if (isCheckboxChecked) {
      this.inputError[fieldName] = false;
      return;
    }
    
    const displayValue = this.getDisplayValue(fieldName);
    if (displayValue === '') {
      this.inputError[fieldName] = true;
      return;
    }
    
    const isError = !(value >= min && value <= max);
    this.inputError[fieldName] = isError;
  }
  
  private getDisplayValue(fieldName: string): number | string {
    switch (fieldName) {
      case 'presenceLevel':
        return this.presenceLevelDisplay;
      case 'presenceFadeTime':
        return this.presenceFadeTimeDisplay;
      case 'presenceHoldTime':
        return this.presenceHoldTimeDisplay;
      case 'noPresenceLevel':
        return this.noPresenceLevelDisplay;
      case 'noPresenceFadeTime':
        return this.noPresenceFadeTimeDisplay;
      case 'noPresenceProlongTime':
        return this.noPresenceProlongTimeDisplay;
      case 'sunsetThreshold':
        return this.sunsetThresholdDisplay;
      case 'sunriseThreshold':
        return this.sunriseThresholdDisplay;
      default:
        return '';
    }
  }

  private loadFromSchema(): void {
    const enabled = this.properties['Enabled'];
    const presenceLevel = this.properties['PresenceLevel'];
    const presenceFadeTime = this.properties['PresenceFadeTime'];
    const presenceHoldTime = this.properties['PresenceHoldTime'];
    const noPresenceLevel = this.properties['NoPresenceLevel'];
    const noPresenceFadeTime = this.properties['NoPresenceFadeTime'];
    const noPresenceProlongTime = this.properties['NoPresenceProlongTime'];
    const sunsetThreshold = this.properties['SunsetThreshold'];
    const sunriseThreshold = this.properties['SunriseThreshold'];

    // Extract Enabled from schema
    if (enabled) {
      this.enabledDefault = enabled.default;
      this.enabled = this.enabledDefault;
      this.isEnableFeature = this.enabledDefault;
      this.isEnableFeatureDefault = this.enabledDefault;
    }

    // Extract PresenceLevel from schema (has oneOf with const for dynadimmer and range)
    if (presenceLevel) {
      this.presenceLevelDefault = presenceLevel.default;
      this.presenceLevelHasDynadimmer = !!presenceLevel.oneOf;

      if (presenceLevel.oneOf) {
        const constObj = presenceLevel.oneOf.find((item: any) => item.const !== undefined);
        const rangeObj = presenceLevel.oneOf.find((item: any) => item.minimum !== undefined);

        if (constObj) {
          this.presenceLevelDynadimmerValue = constObj.const;
          this.presenceLevelDynadimmerValueDefault = constObj.const;
        }
        if (rangeObj) {
          this.presenceLevelMin = rangeObj.minimum;
          this.presenceLevelMax = rangeObj.maximum;
        }
      } else {
        this.presenceLevelMin = presenceLevel.minimum;
        this.presenceLevelMax = presenceLevel.maximum;
      }

      // Set checkbox default based on whether default value equals dynadimmer const value
      if (this.presenceLevelDefault === this.presenceLevelDynadimmerValue) {
        this.presenceLevelUseDynadimmerDefault = true;
        this.presenceLevelUseDynadimmer = true;
        this.presenceLevel = this.presenceLevelDynadimmerValue;
        this.presenceLevelDisplay = '';
      } else {
        this.presenceLevelUseDynadimmerDefault = false;
        this.presenceLevelUseDynadimmer = false;
        this.presenceLevel = this.presenceLevelDefault;
        const isValid = this.presenceLevelDefault >= this.presenceLevelMin && this.presenceLevelDefault <= this.presenceLevelMax;
        this.presenceLevelDisplay = isValid ? this.presenceLevelDefault : '';
      }
      this.presenceLevelOptions = { floor: this.presenceLevelMin, ceil: this.presenceLevelMax, showSelectionBar: true };
    }

    if (presenceFadeTime) {
      this.presenceFadeTimeMin = presenceFadeTime.minimum;
      this.presenceFadeTimeMax = presenceFadeTime.maximum;
      this.presenceFadeTimeDefault = presenceFadeTime.default;
      this.presenceFadeTime = this.presenceFadeTimeDefault;
      const isValid = this.presenceFadeTimeDefault >= this.presenceFadeTimeMin && this.presenceFadeTimeDefault <= this.presenceFadeTimeMax;
      this.presenceFadeTimeDisplay = isValid ? this.presenceFadeTimeDefault : '';
      this.presenceFadeTimeOptions = { floor: this.presenceFadeTimeMin, ceil: this.presenceFadeTimeMax, showSelectionBar: true };
    }

    if (presenceHoldTime) {
      this.presenceHoldTimeDefault = presenceHoldTime.default;

      if (presenceHoldTime.oneOf) {
        const rangeObj = presenceHoldTime.oneOf.find((item: any) => item.minimum !== undefined);
        const constObj = presenceHoldTime.oneOf.find((item: any) => item.const !== undefined);

        if (rangeObj) {
          this.presenceHoldTimeMin = rangeObj.minimum;
          this.presenceHoldTimeMax = rangeObj.maximum;
          const step = rangeObj.multipleOf;
          this.presenceHoldTimeOptions = {
            floor: this.presenceHoldTimeMin,
            ceil: this.presenceHoldTimeMax,
            step: step,
            showSelectionBar: true
          };
        }

        if (constObj) {
          this.presenceHoldTimeInfiniteValue = constObj.const;
        }
      } else {
        this.presenceHoldTimeMin = presenceHoldTime.minimum;
        this.presenceHoldTimeMax = presenceHoldTime.maximum;
        this.presenceHoldTimeOptions = { floor: this.presenceHoldTimeMin, ceil: this.presenceHoldTimeMax, showSelectionBar: true };
      }

      if (this.presenceHoldTimeDefault === this.presenceHoldTimeInfiniteValue) {
        this.presenceHoldTimeInfiniteDefault = true;
        this.presenceHoldTimeInfinite = true;
        this.presenceHoldTime = this.presenceHoldTimeInfiniteValue;
        this.presenceHoldTimeDisplay = '';
      } else {
        this.presenceHoldTimeInfiniteDefault = false;
        this.presenceHoldTimeInfinite = false;
        this.presenceHoldTime = this.presenceHoldTimeDefault;
        const isValid = this.presenceHoldTimeDefault >= this.presenceHoldTimeMin && this.presenceHoldTimeDefault <= this.presenceHoldTimeMax;
        this.presenceHoldTimeDisplay = isValid ? this.presenceHoldTimeDefault : '';
      }
    }

    if (noPresenceLevel) {
      this.noPresenceLevelDefault = noPresenceLevel.default;
      this.noPresenceLevelHasDynadimmer = !!noPresenceLevel.oneOf;

      if (noPresenceLevel.oneOf) {
        const constObj = noPresenceLevel.oneOf.find((item: any) => item.const !== undefined);
        const rangeObj = noPresenceLevel.oneOf.find((item: any) => item.minimum !== undefined);

        if (constObj) {
          this.noPresenceLevelDynadimmerValue = constObj.const;
          this.noPresenceLevelDynadimmerValueDefault = constObj.const;
        }
        if (rangeObj) {
          this.noPresenceLevelMin = rangeObj.minimum;
          this.noPresenceLevelMax = rangeObj.maximum;
        }
      } else {
        this.noPresenceLevelMin = noPresenceLevel.minimum;
        this.noPresenceLevelMax = noPresenceLevel.maximum;
      }

      if (this.noPresenceLevelDefault === this.noPresenceLevelDynadimmerValue) {
        this.noPresenceLevelUseDynadimmerDefault = true;
        this.noPresenceLevelUseDynadimmer = true;
        this.noPresenceLevel = this.noPresenceLevelDynadimmerValue;
        this.noPresenceLevelDisplay = '';
      } else {
        this.noPresenceLevelUseDynadimmerDefault = false;
        this.noPresenceLevelUseDynadimmer = false;
        this.noPresenceLevel = this.noPresenceLevelDefault;
        const isValid = this.noPresenceLevelDefault >= this.noPresenceLevelMin && this.noPresenceLevelDefault <= this.noPresenceLevelMax;
        this.noPresenceLevelDisplay = isValid ? this.noPresenceLevelDefault : '';
      }
      this.noPresenceLevelOptions = { floor: this.noPresenceLevelMin, ceil: this.noPresenceLevelMax, showSelectionBar: true };
    }

    if (noPresenceFadeTime) {
      this.noPresenceFadeTimeMin = noPresenceFadeTime.minimum;
      this.noPresenceFadeTimeMax = noPresenceFadeTime.maximum;
      this.noPresenceFadeTimeDefault = noPresenceFadeTime.default;
      this.noPresenceFadeTime = this.noPresenceFadeTimeDefault;
      const isValid = this.noPresenceFadeTimeDefault >= this.noPresenceFadeTimeMin && this.noPresenceFadeTimeDefault <= this.noPresenceFadeTimeMax;
      this.noPresenceFadeTimeDisplay = isValid ? this.noPresenceFadeTimeDefault : '';
      this.noPresenceFadeTimeOptions = { floor: this.noPresenceFadeTimeMin, ceil: this.noPresenceFadeTimeMax, showSelectionBar: true };
    }

    if (noPresenceProlongTime) {
      this.noPresenceProlongTimeDefault = noPresenceProlongTime.default;

      if (noPresenceProlongTime.oneOf) {
        const rangeObj = noPresenceProlongTime.oneOf.find((item: any) => item.minimum !== undefined);
        const constObj = noPresenceProlongTime.oneOf.find((item: any) => item.const !== undefined);

        if (rangeObj) {
          this.noPresenceProlongTimeMin = rangeObj.minimum;
          this.noPresenceProlongTimeMax = rangeObj.maximum;
          const step = rangeObj.multipleOf;
          this.noPresenceProlongTimeOptions = {
            floor: this.noPresenceProlongTimeMin,
            ceil: this.noPresenceProlongTimeMax,
            step: step,
            showSelectionBar: true
          };
        }

        if (constObj) {
          this.noPresenceProlongTimeInfiniteValue = constObj.const;
        }
      } else {
        this.noPresenceProlongTimeMin = noPresenceProlongTime.minimum;
        this.noPresenceProlongTimeMax = noPresenceProlongTime.maximum;
        this.noPresenceProlongTimeOptions = { floor: this.noPresenceProlongTimeMin, ceil: this.noPresenceProlongTimeMax, showSelectionBar: true };
      }

      if (this.noPresenceProlongTimeDefault === this.noPresenceProlongTimeInfiniteValue) {
        this.noPresenceProlongTimeInfiniteDefault = true;
        this.noPresenceProlongTimeInfinite = true;
        this.noPresenceProlongTime = this.noPresenceProlongTimeInfiniteValue;
        this.noPresenceProlongTimeDisplay = '';
      } else {
        this.noPresenceProlongTimeInfiniteDefault = false;
        this.noPresenceProlongTimeInfinite = false;
        this.noPresenceProlongTime = this.noPresenceProlongTimeDefault;
        const isValid = this.noPresenceProlongTimeDefault >= this.noPresenceProlongTimeMin && this.noPresenceProlongTimeDefault <= this.noPresenceProlongTimeMax;
        this.noPresenceProlongTimeDisplay = isValid ? this.noPresenceProlongTimeDefault : '';
      }
    }

    if (sunsetThreshold) {
      this.sunsetThresholdHasOneOf = !!sunsetThreshold.oneOf;

      if (sunsetThreshold.oneOf) {
        const rangeObj = sunsetThreshold.oneOf.find((item: any) => item.minimum !== undefined);
        const constObj = sunsetThreshold.oneOf.find((item: any) => item.const !== undefined);

        if (rangeObj) {
          this.sunsetThresholdMin = rangeObj.minimum;
          this.sunsetThresholdMax = rangeObj.maximum;
        }

        if (constObj !== undefined) {
          this.sunsetThresholdLightSensorValue = constObj.const;
        }

        this.sunsetThresholdOptions = { floor: this.sunsetThresholdMin, ceil: this.sunsetThresholdMax, showSelectionBar: true };
      } else if (sunsetThreshold.const !== undefined) {
        // Handle const-only case (no oneOf, no min/max range)
        this.sunsetThresholdLightSensorValue = sunsetThreshold.const;
        this.sunsetThresholdMin = sunsetThreshold.const;
        this.sunsetThresholdMax = sunsetThreshold.const;
        // If no default is specified, use const value as default
        this.sunsetThresholdDefault = sunsetThreshold.default !== undefined ? sunsetThreshold.default : sunsetThreshold.const;
        this.sunsetThresholdOptions = { floor: this.sunsetThresholdMin, ceil: this.sunsetThresholdMax, showSelectionBar: true };
      } else {
        // Has min/max range
        this.sunsetThresholdMin = sunsetThreshold.minimum;
        this.sunsetThresholdMax = sunsetThreshold.maximum;
        this.sunsetThresholdDefault = sunsetThreshold.default;
        this.sunsetThresholdOptions = { floor: this.sunsetThresholdMin, ceil: this.sunsetThresholdMax, showSelectionBar: true };
      }

      // Ensure default is set if not already
      if (this.sunsetThresholdDefault === undefined) {
        this.sunsetThresholdDefault = sunsetThreshold.default !== undefined ? sunsetThreshold.default : this.sunsetThresholdLightSensorValue;
      }

      this.sunsetThreshold = this.sunsetThresholdDefault;
      if (this.sunsetThresholdDefault === this.sunsetThresholdLightSensorValue) {
        this.sunsetThresholdDisplay = '';
      } else {
        const isValid = this.sunsetThresholdDefault >= this.sunsetThresholdMin && this.sunsetThresholdDefault <= this.sunsetThresholdMax;
        this.sunsetThresholdDisplay = isValid ? this.sunsetThresholdDefault : '';
      }
    }

    if (sunriseThreshold) {
      this.sunriseThresholdHasOneOf = !!sunriseThreshold.oneOf;

      if (sunriseThreshold.oneOf) {
        const rangeObj = sunriseThreshold.oneOf.find((item: any) => item.minimum !== undefined);
        const constObj = sunriseThreshold.oneOf.find((item: any) => item.const !== undefined);

        if (rangeObj) {
          this.sunriseThresholdMin = rangeObj.minimum;
          this.sunriseThresholdMax = rangeObj.maximum;
        }

        if (constObj !== undefined) {
          this.sunriseThresholdLightSensorValue = constObj.const;
        }

        this.sunriseThresholdOptions = { floor: this.sunriseThresholdMin, ceil: this.sunriseThresholdMax, showSelectionBar: true };
      } else if (sunriseThreshold.const !== undefined) {
        // Handle const-only case (no oneOf, no min/max range)
        this.sunriseThresholdLightSensorValue = sunriseThreshold.const;
        this.sunriseThresholdMin = sunriseThreshold.const;
        this.sunriseThresholdMax = sunriseThreshold.const;
        // If no default is specified, use const value as default
        this.sunriseThresholdDefault = sunriseThreshold.default !== undefined ? sunriseThreshold.default : sunriseThreshold.const;
        this.sunriseThresholdOptions = { floor: this.sunriseThresholdMin, ceil: this.sunriseThresholdMax, showSelectionBar: true };
      } else {
        // Has min/max range
        this.sunriseThresholdMin = sunriseThreshold.minimum;
        this.sunriseThresholdMax = sunriseThreshold.maximum;
        this.sunriseThresholdDefault = sunriseThreshold.default;
        this.sunriseThresholdOptions = { floor: this.sunriseThresholdMin, ceil: this.sunriseThresholdMax, showSelectionBar: true };
      }

      // Ensure default is set if not already
      if (this.sunriseThresholdDefault === undefined) {
        this.sunriseThresholdDefault = sunriseThreshold.default !== undefined ? sunriseThreshold.default : this.sunriseThresholdLightSensorValue;
      }

      this.sunriseThreshold = this.sunriseThresholdDefault;
      if (this.sunriseThresholdDefault === this.sunriseThresholdLightSensorValue) {
        this.sunriseThresholdDisplay = '';
      } else {
        const isValid = this.sunriseThresholdDefault >= this.sunriseThresholdMin && this.sunriseThresholdDefault <= this.sunriseThresholdMax;
        this.sunriseThresholdDisplay = isValid ? this.sunriseThresholdDefault : '';
      }
    }

    if (this.sunsetThresholdDefault === this.sunsetThresholdLightSensorValue && 
        this.sunriseThresholdDefault === this.sunriseThresholdLightSensorValue) {
      this.letLightSensorControlDefault = false;
      this.letLightSensorControl = false;
      this.sunsetThresholdDisplay = '';
      this.sunriseThresholdDisplay = '';
    } else {
      this.letLightSensorControlDefault = true;
      this.letLightSensorControl = true;
    }
  }

  private setupInputDebouncing(): void {
    this.presenceLevelInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'presenceLevel', this.presenceLevelMin, this.presenceLevelMax));
    this.presenceFadeTimeInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'presenceFadeTime', this.presenceFadeTimeMin, this.presenceFadeTimeMax));
    this.presenceHoldTimeInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'presenceHoldTime', this.presenceHoldTimeMin, this.presenceHoldTimeMax));
    this.noPresenceLevelInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'noPresenceLevel', this.noPresenceLevelMin, this.noPresenceLevelMax));
    this.noPresenceFadeTimeInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'noPresenceFadeTime', this.noPresenceFadeTimeMin, this.noPresenceFadeTimeMax));
    this.noPresenceProlongTimeInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'noPresenceProlongTime', this.noPresenceProlongTimeMin, this.noPresenceProlongTimeMax));
    this.sunsetThresholdInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'sunsetThreshold', this.sunsetThresholdMin, this.sunsetThresholdMax));
    this.sunriseThresholdInput$.pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(v => this.applyInputValue(v, 'sunriseThreshold', this.sunriseThresholdMin, this.sunriseThresholdMax));
  }

  onSliderChange(value: number, fieldName: string): void {
    this.ignoreParentUpdate = true;
    this.inputError[fieldName] = false;

    switch (fieldName) {
      case 'presenceLevel':
        this.presenceLevel = value;
        this.presenceLevelDisplay = value; 
        break;
      case 'presenceFadeTime':
        this.presenceFadeTime = value;
        this.presenceFadeTimeDisplay = value; 
        break;
      case 'presenceHoldTime':
        this.presenceHoldTime = value;
        this.presenceHoldTimeDisplay = value; 
        break;
      case 'noPresenceLevel':
        this.noPresenceLevel = value;
        this.noPresenceLevelDisplay = value; 
        break;
      case 'noPresenceFadeTime':
        this.noPresenceFadeTime = value;
        this.noPresenceFadeTimeDisplay = value; 
        break;
      case 'noPresenceProlongTime':
        this.noPresenceProlongTime = value;
        this.noPresenceProlongTimeDisplay = value; 
        break;
      case 'sunsetThreshold':
        this.sunsetThreshold = value;
        this.sunsetThresholdDisplay = value; 
        break;
      case 'sunriseThreshold':
        this.sunriseThreshold = value;
        this.sunriseThresholdDisplay = value; 
        break;
    }

    this.recalcErrorState();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableDscCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  onFieldInput(value: string, fieldName: string): void {
    switch (fieldName) {
      case 'presenceLevel':
        this.presenceLevelInput$.next(value);
        break;
      case 'presenceFadeTime':
        this.presenceFadeTimeInput$.next(value);
        break;
      case 'presenceHoldTime':
        this.presenceHoldTimeInput$.next(value);
        break;
      case 'noPresenceLevel':
        this.noPresenceLevelInput$.next(value);
        break;
      case 'noPresenceFadeTime':
        this.noPresenceFadeTimeInput$.next(value);
        break;
      case 'noPresenceProlongTime':
        this.noPresenceProlongTimeInput$.next(value);
        break;
      case 'sunsetThreshold':
        this.sunsetThresholdInput$.next(value);
        break;
      case 'sunriseThreshold':
        this.sunriseThresholdInput$.next(value);
        break;
    }
  }

  onFieldBlur(inputEl: HTMLInputElement, fieldName: string): void {
    this.isBlurring = true;
    let num = parseFloat(inputEl.value);
    
    // For presenceHoldTime and noPresenceProlongTime, apply step rounding only if within valid range
    if (!isNaN(num) && (fieldName === 'presenceHoldTime' || fieldName === 'noPresenceProlongTime')) {
      const options = fieldName === 'presenceHoldTime' ? this.presenceHoldTimeOptions : this.noPresenceProlongTimeOptions;
      const step = options.step;
      const min = fieldName === 'presenceHoldTime' ? this.presenceHoldTimeMin : this.noPresenceProlongTimeMin;
      const max = fieldName === 'presenceHoldTime' ? this.presenceHoldTimeMax : this.noPresenceProlongTimeMax;
      const constValue = fieldName === 'presenceHoldTime' ? this.presenceHoldTimeInfiniteValue : this.noPresenceProlongTimeInfiniteValue;
      
      // Only round and clamp if value is within acceptable range or equals const
      if ((num >= min && num <= max) || num === constValue) {
        // Apply step rounding
        if (num !== constValue) {
          num = Math.round(num / step) * step;
          num = Math.max(min, Math.min(max, num));
        }
      }
      // If out of range and not const value, keep the invalid value to show error
    }
    
    const cleaned = isNaN(num) ? '' : String(num);
    inputEl.value = '';
    inputEl.value = cleaned;

    switch (fieldName) {
      case 'presenceLevel':
        this.presenceLevelDisplay = isNaN(num) ? '' : num;
        if (!isNaN(num)) {
          this.presenceLevel = num;
        }
        break;
      case 'presenceFadeTime':
        this.presenceFadeTimeDisplay = isNaN(num) ? '' : num;
        if (!isNaN(num)) {
          this.presenceFadeTime = num;
        }
        break;
      case 'presenceHoldTime':
        this.presenceHoldTimeDisplay = isNaN(num) ? '' : num;
        if (!isNaN(num)) {
          this.presenceHoldTime = num;
        }
        break;
      case 'noPresenceLevel':
        this.noPresenceLevelDisplay = isNaN(num) ? '' : num;
        if (!isNaN(num)) {
          this.noPresenceLevel = num;
        }
        break;
      case 'noPresenceFadeTime':
        this.noPresenceFadeTimeDisplay = isNaN(num) ? '' : num;
        if (!isNaN(num)) {
          this.noPresenceFadeTime = num;
        }
        break;
      case 'noPresenceProlongTime':
        this.noPresenceProlongTimeDisplay = isNaN(num) ? '' : num;
        if (!isNaN(num)) {
          this.noPresenceProlongTime = num;
        }
        break;
      case 'sunsetThreshold':
        this.sunsetThresholdDisplay = isNaN(num) ? '' : num;
        if (!isNaN(num)) {
          this.sunsetThreshold = num;
        }
        break;
      case 'sunriseThreshold':
        this.sunriseThresholdDisplay = isNaN(num) ? '' : num;
        if (!isNaN(num)) {
          this.sunriseThreshold = num;
        }
        break;
    }
    
    this.revalidateAllFields();
    this.recalcErrorState();
    this.checkDefaults();
    
    this.updateFormControl();
    this.sendErrorStateToParent();
    setTimeout(() => { this.isBlurring = false; }, 150);
  }

  private applyInputValue(value: string, fieldName: string, min: number, max: number): void {
    if (this.isResetting || this.isBlurring) { return; }

    this.ignoreParentUpdate = true;
    let num = parseInt(value, 10);


    switch (fieldName) {
      case 'presenceLevel':
        this.presenceLevel = num;
        this.presenceLevelDisplay = num;
        break;
      case 'presenceFadeTime':
        this.presenceFadeTime = num;
        this.presenceFadeTimeDisplay = num;
        break;
      case 'presenceHoldTime':
        this.presenceHoldTime = num;
        this.presenceHoldTimeDisplay = num;
        break;
      case 'noPresenceLevel':
        this.noPresenceLevel = num;
        this.noPresenceLevelDisplay = num;
        break;
      case 'noPresenceFadeTime':
        this.noPresenceFadeTime = num;
        this.noPresenceFadeTimeDisplay = num;
        break;
      case 'noPresenceProlongTime':
        this.noPresenceProlongTime = num;
        this.noPresenceProlongTimeDisplay = num;
        break;
      case 'sunsetThreshold':
        this.sunsetThreshold = num;
        this.sunsetThresholdDisplay = num;
        break;
      case 'sunriseThreshold':
        this.sunriseThreshold = num;
        this.sunriseThresholdDisplay = num;
        break;
    }

    // Validate based on field type
    if (value === '' || isNaN(num)) {
      this.inputError[fieldName] = true;
    } else {
      if (fieldName === 'presenceLevel' || fieldName === 'noPresenceLevel' || 
          fieldName === 'presenceHoldTime' || fieldName === 'noPresenceProlongTime') {
        let isCheckboxChecked = false;
        if (fieldName === 'presenceLevel') {
          isCheckboxChecked = this.presenceLevelUseDynadimmer;
        } else if (fieldName === 'noPresenceLevel') {
          isCheckboxChecked = this.noPresenceLevelUseDynadimmer;
        } else if (fieldName === 'presenceHoldTime') {
          isCheckboxChecked = this.presenceHoldTimeInfinite;
        } else if (fieldName === 'noPresenceProlongTime') {
          isCheckboxChecked = this.noPresenceProlongTimeInfinite;
        }
        if (isCheckboxChecked) {
          this.inputError[fieldName] = false;
        } else {
          this.inputError[fieldName] = num < min || num > max;
        }
      } else if (fieldName === 'sunsetThreshold') {
        const isInRange = num >= min && num <= max;
        const isConstValue = !this.letLightSensorControl && num === this.sunsetThresholdLightSensorValue;
        this.inputError[fieldName] = !(isInRange || isConstValue);
      } else if (fieldName === 'sunriseThreshold') {
        const isInRange = num >= min && num <= max;
        const isConstValue = !this.letLightSensorControl && num === this.sunriseThresholdLightSensorValue;
        this.inputError[fieldName] = !(isInRange || isConstValue);
      } else {
        this.inputError[fieldName] = num < min || num > max;
      }
    }

    this.recalcErrorState();
    this.checkDefaults();
    
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableDscCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  onInfiniteCheckboxChange(event: Event, type: 'presence' | 'noPresence'): void {
    this.ignoreParentUpdate = true;
    const checked = (event.target as HTMLInputElement).checked;

    if (type === 'presence') {
      this.presenceHoldTimeInfinite = checked;
      if (checked) {
        this.presenceHoldTime = this.presenceHoldTimeInfiniteValue;
        this.presenceHoldTimeDisplay = '';
        this.inputError['presenceHoldTime'] = false;
      } else {
        // When unchecking: if default equals infinite const, use max value instead
        if (this.presenceHoldTimeDefault === this.presenceHoldTimeInfiniteValue) {
          this.presenceHoldTime = this.presenceHoldTimeMax;
          this.presenceHoldTimeDisplay = this.presenceHoldTimeMax;
        } else {
          this.presenceHoldTime = this.presenceHoldTimeDefault;
          const isValid = this.presenceHoldTimeDefault >= this.presenceHoldTimeMin && this.presenceHoldTimeDefault <= this.presenceHoldTimeMax;
          this.presenceHoldTimeDisplay = isValid ? this.presenceHoldTimeDefault : '';
        }
      }
      this.presenceHoldTimeOptions = Object.assign({}, this.presenceHoldTimeOptions, { disabled: this.dscDisabled || checked });
    } else {
      this.noPresenceProlongTimeInfinite = checked;
      if (checked) {
        this.noPresenceProlongTime = this.noPresenceProlongTimeInfiniteValue;
        this.noPresenceProlongTimeDisplay = '';
        this.inputError['noPresenceProlongTime'] = false;
      } else {
        // When unchecking: if default equals infinite const, use max value instead
        if (this.noPresenceProlongTimeDefault === this.noPresenceProlongTimeInfiniteValue) {
          this.noPresenceProlongTime = this.noPresenceProlongTimeMax;
          this.noPresenceProlongTimeDisplay = this.noPresenceProlongTimeMax;
        } else {
          this.noPresenceProlongTime = this.noPresenceProlongTimeDefault;
          const isValid = this.noPresenceProlongTimeDefault >= this.noPresenceProlongTimeMin && this.noPresenceProlongTimeDefault <= this.noPresenceProlongTimeMax;
          this.noPresenceProlongTimeDisplay = isValid ? this.noPresenceProlongTimeDefault : '';
        }
      }
      this.noPresenceProlongTimeOptions = Object.assign({}, this.noPresenceProlongTimeOptions, { disabled: this.dscDisabled || checked });
    }

    this.recalcErrorState();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableDscCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  onEnabledCheckboxChange(event: Event): void {
    this.ignoreParentUpdate = true;
    const checked = (event.target as HTMLInputElement).checked;
    this.enabled = checked;
    this.isEnableFeature = checked;

    // Call enableFeatureChanged to handle disabling/enabling all controls
    this.enableFeatureChanged(checked);

    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableDscCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  onDynadimmerCheckboxChange(event: Event, type: 'presence' | 'noPresence'): void {
    this.ignoreParentUpdate = true;
    const checked = (event.target as HTMLInputElement).checked;

    if (type === 'presence') {
      this.presenceLevelUseDynadimmer = checked;
      // Update slider options to enable/disable the slider (consider both dscDisabled and dynadimmer)
      this.presenceLevelOptions = Object.assign({}, this.presenceLevelOptions, { disabled: this.dscDisabled || checked });

      if (checked) {
        // Set value to dynadimmer const from schema, clear display
        this.presenceLevel = this.presenceLevelDynadimmerValue;
        this.presenceLevelDisplay = '';
        this.inputError['presenceLevel'] = false;
      } else {
        // Restore to default value from schema
        this.presenceLevel = this.presenceLevelDefault;
        const isValid = this.presenceLevelDefault >= this.presenceLevelMin && this.presenceLevelDefault <= this.presenceLevelMax;
        this.presenceLevelDisplay = isValid ? this.presenceLevelDefault : '';
      }
    } else {
      this.noPresenceLevelUseDynadimmer = checked;
      // Update slider options to enable/disable the slider (consider both dscDisabled and dynadimmer)
      this.noPresenceLevelOptions = Object.assign({}, this.noPresenceLevelOptions, { disabled: this.dscDisabled || checked });

      if (checked) {
        // Set value to dynadimmer const from schema, clear display
        this.noPresenceLevel = this.noPresenceLevelDynadimmerValue;
        this.noPresenceLevelDisplay = '';
        this.inputError['noPresenceLevel'] = false;
      } else {
        // Restore to default value from schema
        this.noPresenceLevel = this.noPresenceLevelDefault;
        const isValid = this.noPresenceLevelDefault >= this.noPresenceLevelMin && this.noPresenceLevelDefault <= this.noPresenceLevelMax;
        this.noPresenceLevelDisplay = isValid ? this.noPresenceLevelDefault : '';
      }
    }

    this.recalcErrorState();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableDscCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  onLightSensorCheckboxChange(event: Event): void{
    this.ignoreParentUpdate = true;
    const checked = (event.target as HTMLInputElement).checked;
    this.letLightSensorControl = checked;

    if (checked) {
      // When checked: enable sliders so user can set threshold values
      // If default equals const (0), use max value instead
      if (this.sunsetThresholdDefault === this.sunsetThresholdLightSensorValue) {
        this.sunsetThreshold = this.sunsetThresholdMax;
        this.sunsetThresholdDisplay = this.sunsetThresholdMax;
      } else {
        this.sunsetThreshold = this.sunsetThresholdDefault;
        const isValid = this.sunsetThresholdDefault >= this.sunsetThresholdMin && this.sunsetThresholdDefault <= this.sunsetThresholdMax;
        this.sunsetThresholdDisplay = isValid ? this.sunsetThresholdDefault : '';
      }
      
      if (this.sunriseThresholdDefault === this.sunriseThresholdLightSensorValue) {
        this.sunriseThreshold = this.sunriseThresholdMax;
        this.sunriseThresholdDisplay = this.sunriseThresholdMax;
      } else {
        this.sunriseThreshold = this.sunriseThresholdDefault;
        const isValid = this.sunriseThresholdDefault >= this.sunriseThresholdMin && this.sunriseThresholdDefault <= this.sunriseThresholdMax;
        this.sunriseThresholdDisplay = isValid ? this.sunriseThresholdDefault : '';
      }

      // Update slider options to enable (not controlled by dscDisabled)
      this.sunsetThresholdOptions = Object.assign({}, this.sunsetThresholdOptions, { disabled: false });
      this.sunriseThresholdOptions = Object.assign({}, this.sunriseThresholdOptions, { disabled: false });
    } else {
      // When unchecked: set values to const (0), clear displays, disable sliders
      this.sunsetThreshold = this.sunsetThresholdLightSensorValue;
      this.sunsetThresholdDisplay = '';
      this.sunriseThreshold = this.sunriseThresholdLightSensorValue;
      this.sunriseThresholdDisplay = '';
      
      // Clear any validation errors since we're setting to valid const values
      this.inputError['sunsetThreshold'] = false;
      this.inputError['sunriseThreshold'] = false;

      this.sunsetThresholdOptions = Object.assign({}, this.sunsetThresholdOptions, { disabled: true });
      this.sunriseThresholdOptions = Object.assign({}, this.sunriseThresholdOptions, { disabled: true });
    }

    // Revalidate fields to update error states based on new checkbox state
    this.revalidateAllFields();
    this.recalcErrorState();
    this.checkDefaults();
    this.updateFormControl();
    if (!this.defaultColor) {
      this.checkboxState.enableDscCheckbox();
      this.isIncludedInConfiguration = true;
    }
    this.sendErrorStateToParent();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  enableFeatureChanged(checked: boolean): void {
    this.ignoreParentUpdate = true;
    this.isEnableFeature = checked;
    this.dscDisabled = !checked;
    this.presenceLevelOptions = Object.assign({}, this.presenceLevelOptions, { disabled: this.dscDisabled || this.presenceLevelUseDynadimmer });
    this.presenceFadeTimeOptions = Object.assign({}, this.presenceFadeTimeOptions, { disabled: this.dscDisabled });
    this.presenceHoldTimeOptions = Object.assign({}, this.presenceHoldTimeOptions, { disabled: this.dscDisabled || this.presenceHoldTimeInfinite });
    this.noPresenceLevelOptions = Object.assign({}, this.noPresenceLevelOptions, { disabled: this.dscDisabled || this.noPresenceLevelUseDynadimmer });
    this.noPresenceFadeTimeOptions = Object.assign({}, this.noPresenceFadeTimeOptions, { disabled: this.dscDisabled });
    this.noPresenceProlongTimeOptions = Object.assign({}, this.noPresenceProlongTimeOptions, { disabled: this.dscDisabled || this.noPresenceProlongTimeInfinite });
    // Sunrise/Sunset sliders should always be editable (only controlled by letLightSensorControl checkbox)
    // This allows users to fix out-of-range values even when Enable feature checkbox is unchecked
    this.sunsetThresholdOptions = Object.assign({}, this.sunsetThresholdOptions, { disabled: !this.letLightSensorControl });
    this.sunriseThresholdOptions = Object.assign({}, this.sunriseThresholdOptions, { disabled: !this.letLightSensorControl });

    this.checkDefaults();

    if (!this.defaultColor) {
      this.checkboxState.enableDscCheckbox();
      this.isIncludedInConfiguration = true;
    }

    this.sendErrorStateToParent();
    this.updateFormControl();
    this.ref.detectChanges();
    this.ignoreParentUpdate = false;
  }

  getSliderSafeValue(value: number, min: number, max: number): number {
    if (value === null || value === undefined || isNaN(value)) { return min; }
    if (value < min) { return min; }
    if (value > max) { return max; }
    return value;
  }

  private checkDefaults(): void {
    this.defaultColor = (
      this.isEnableFeature === this.isEnableFeatureDefault &&
      this.enabled === this.enabledDefault &&
      this.presenceLevel === this.presenceLevelDefault &&
      this.presenceLevelUseDynadimmer === this.presenceLevelUseDynadimmerDefault &&
      this.presenceFadeTime === this.presenceFadeTimeDefault &&
      this.presenceHoldTime === this.presenceHoldTimeDefault &&
      this.presenceHoldTimeInfinite === this.presenceHoldTimeInfiniteDefault &&
      this.noPresenceLevel === this.noPresenceLevelDefault &&
      this.noPresenceLevelUseDynadimmer === this.noPresenceLevelUseDynadimmerDefault &&
      this.noPresenceFadeTime === this.noPresenceFadeTimeDefault &&
      this.noPresenceProlongTime === this.noPresenceProlongTimeDefault &&
      this.noPresenceProlongTimeInfinite === this.noPresenceProlongTimeInfiniteDefault &&
      this.sunsetThreshold === this.sunsetThresholdDefault &&
      this.sunriseThreshold === this.sunriseThresholdDefault &&
      this.letLightSensorControl === this.letLightSensorControlDefault
    );
    this.featureDefaults.setDscDefaultColor(this.defaultColor);
  }

  resetToDefaults(): void {
    this.isResetting = true;

    // Preserve checkbox state before resetting
    const wasIncludedInConfiguration = this.isIncludedInConfiguration;

    this.isEnableFeature = this.isEnableFeatureDefault;
    this.enabled = this.enabledDefault;
    
    // Reset presenceLevel with validation
    this.presenceLevel = this.presenceLevelDefault;
    this.presenceLevelUseDynadimmer = this.presenceLevelUseDynadimmerDefault;
    this.presenceLevelDynadimmerValue = this.presenceLevelDynadimmerValueDefault;
    if (this.presenceLevelUseDynadimmerDefault) {
      this.presenceLevelDisplay = '';
    } else {
      const isValid = this.presenceLevelDefault >= this.presenceLevelMin && this.presenceLevelDefault <= this.presenceLevelMax;
      this.presenceLevelDisplay = isValid ? this.presenceLevelDefault : '';
    }
    
    // Reset presenceFadeTime with validation
    this.presenceFadeTime = this.presenceFadeTimeDefault;
    const presenceFadeValid = this.presenceFadeTimeDefault >= this.presenceFadeTimeMin && this.presenceFadeTimeDefault <= this.presenceFadeTimeMax;
    this.presenceFadeTimeDisplay = presenceFadeValid ? this.presenceFadeTimeDefault : '';
    
    // Reset presenceHoldTime with validation
    this.presenceHoldTime = this.presenceHoldTimeDefault;
    this.presenceHoldTimeInfinite = this.presenceHoldTimeInfiniteDefault;
    if (this.presenceHoldTimeInfiniteDefault) {
      this.presenceHoldTimeDisplay = '';
    } else {
      const isValid = this.presenceHoldTimeDefault >= this.presenceHoldTimeMin && this.presenceHoldTimeDefault <= this.presenceHoldTimeMax;
      this.presenceHoldTimeDisplay = isValid ? this.presenceHoldTimeDefault : '';
    }

    // Reset noPresenceLevel with validation
    this.noPresenceLevel = this.noPresenceLevelDefault;
    this.noPresenceLevelUseDynadimmer = this.noPresenceLevelUseDynadimmerDefault;
    this.noPresenceLevelDynadimmerValue = this.noPresenceLevelDynadimmerValueDefault;
    if (this.noPresenceLevelUseDynadimmerDefault) {
      this.noPresenceLevelDisplay = '';
    } else {
      const isValid = this.noPresenceLevelDefault >= this.noPresenceLevelMin && this.noPresenceLevelDefault <= this.noPresenceLevelMax;
      this.noPresenceLevelDisplay = isValid ? this.noPresenceLevelDefault : '';
    }
    
    // Reset noPresenceFadeTime with validation
    this.noPresenceFadeTime = this.noPresenceFadeTimeDefault;
    const noPresenceFadeValid = this.noPresenceFadeTimeDefault >= this.noPresenceFadeTimeMin && this.noPresenceFadeTimeDefault <= this.noPresenceFadeTimeMax;
    this.noPresenceFadeTimeDisplay = noPresenceFadeValid ? this.noPresenceFadeTimeDefault : '';
    
    // Reset noPresenceProlongTime with validation
    this.noPresenceProlongTime = this.noPresenceProlongTimeDefault;
    this.noPresenceProlongTimeInfinite = this.noPresenceProlongTimeInfiniteDefault;
    if (this.noPresenceProlongTimeInfiniteDefault) {
      this.noPresenceProlongTimeDisplay = '';
    } else {
      const isValid = this.noPresenceProlongTimeDefault >= this.noPresenceProlongTimeMin && this.noPresenceProlongTimeDefault <= this.noPresenceProlongTimeMax;
      this.noPresenceProlongTimeDisplay = isValid ? this.noPresenceProlongTimeDefault : '';
    }

    // Reset sunsetThreshold and sunriseThreshold with validation
    this.sunsetThreshold = this.sunsetThresholdDefault;
    this.sunriseThreshold = this.sunriseThresholdDefault;
    this.letLightSensorControl = this.letLightSensorControlDefault;
    
    if (this.letLightSensorControlDefault) {
      // Checkbox is checked - validate and display
      const sunsetValid = this.sunsetThresholdDefault >= this.sunsetThresholdMin && this.sunsetThresholdDefault <= this.sunsetThresholdMax;
      const sunriseValid = this.sunriseThresholdDefault >= this.sunriseThresholdMin && this.sunriseThresholdDefault <= this.sunriseThresholdMax;
      this.sunsetThresholdDisplay = sunsetValid ? this.sunsetThresholdDefault : '';
      this.sunriseThresholdDisplay = sunriseValid ? this.sunriseThresholdDefault : '';
    } else {
      // Checkbox is unchecked - display should be empty
      this.sunsetThresholdDisplay = '';
      this.sunriseThresholdDisplay = '';
    }

    this.dscDisabled = !this.isEnableFeatureDefault;
    this.inputError = {};
    this.hasInputError = false;
    this.dscDisabledButton = false;
    this.defaultColor = true;

    // Update slider options after reset
    this.presenceLevelOptions = Object.assign({}, this.presenceLevelOptions, { disabled: this.dscDisabled || this.presenceLevelUseDynadimmer });
    this.presenceFadeTimeOptions = Object.assign({}, this.presenceFadeTimeOptions, { disabled: this.dscDisabled });
    this.presenceHoldTimeOptions = Object.assign({}, this.presenceHoldTimeOptions, { disabled: this.dscDisabled || this.presenceHoldTimeInfinite });
    this.noPresenceLevelOptions = Object.assign({}, this.noPresenceLevelOptions, { disabled: this.dscDisabled || this.noPresenceLevelUseDynadimmer });
    this.noPresenceFadeTimeOptions = Object.assign({}, this.noPresenceFadeTimeOptions, { disabled: this.dscDisabled });
    this.noPresenceProlongTimeOptions = Object.assign({}, this.noPresenceProlongTimeOptions, { disabled: this.dscDisabled || this.noPresenceProlongTimeInfinite });
    this.sunsetThresholdOptions = Object.assign({}, this.sunsetThresholdOptions, { disabled: this.dscDisabled || this.letLightSensorControl });
    this.sunriseThresholdOptions = Object.assign({}, this.sunriseThresholdOptions, { disabled: this.dscDisabled || this.letLightSensorControl });

    this.featureDefaults.setDscDefaultColor(true);
    this.inputErrors.setDscInputError.next(false);
    this.updateFormControl();

    // Restore checkbox state - if it was checked, keep it checked
    if (wasIncludedInConfiguration) {
      this.isIncludedInConfiguration = true;
      this.checkboxState.enableDscCheckbox();
    }

    this.ref.detectChanges();

    setTimeout(() => { this.isResetting = false; }, 150);
  }

  private recalcErrorState(): void {
    this.dscDisabledButton = false;
    for (const type in this.inputError) {
      if (this.inputError[type]) {
        this.dscDisabledButton = true;
        break;
      }
    }
    this.hasInputError = this.dscDisabledButton;
  }

  private sendErrorStateToParent(): void {
    if (this.isIncludedInConfiguration) {
      this.hasConfigData = true;
      this.inputErrors.setDscInputError.next(this.dscDisabledButton);
    } else {
      this.inputErrors.setDscInputError.next(false);
    }
  }

  private updateFormControl(): void {
    if (!this.formControl) { return; }

    // Build payload - include checkbox states and display values for state restoration
    // Display values preserve user's exact input, even when invalid (error state)
    const value: any = {
      Enabled: this.enabled,
      PresenceLevel: this.presenceLevel,
      PresenceLevelDisplay: this.presenceLevelDisplay,
      PresenceLevelUseDynadimmer: this.presenceLevelUseDynadimmer,
      PresenceFadeTime: this.presenceFadeTime,
      PresenceFadeTimeDisplay: this.presenceFadeTimeDisplay,
      PresenceHoldTime: this.presenceHoldTime,
      PresenceHoldTimeDisplay: this.presenceHoldTimeDisplay,
      PresenceHoldTimeInfinite: this.presenceHoldTimeInfinite,
      NoPresenceLevel: this.noPresenceLevel,
      NoPresenceLevelDisplay: this.noPresenceLevelDisplay,
      NoPresenceLevelUseDynadimmer: this.noPresenceLevelUseDynadimmer,
      NoPresenceFadeTime: this.noPresenceFadeTime,
      NoPresenceFadeTimeDisplay: this.noPresenceFadeTimeDisplay,
      NoPresenceProlongTime: this.noPresenceProlongTime,
      NoPresenceProlongTimeDisplay: this.noPresenceProlongTimeDisplay,
      NoPresenceProlongTimeInfinite: this.noPresenceProlongTimeInfinite,
      SunsetThreshold: this.sunsetThreshold,
      SunsetThresholdDisplay: this.sunsetThresholdDisplay,
      SunriseThreshold: this.sunriseThreshold,
      SunriseThresholdDisplay: this.sunriseThresholdDisplay,
      LetLightSensorControl: this.letLightSensorControl
    };

    this.formControl.setValue(value);
    this.setConfigurationData(value);
  }

  private setConfigurationData(value: any): void {
    if (value != null) {
      const dscObj = Object.assign({}, value);
      this.appStateService.setFeatureData('DSC', dscObj);
    }
  }

  private getNumber(value: any, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  numberOnly(event: KeyboardEvent): void {
    const key = event.key;
    if (!/[0-9]/.test(key) && key !== 'Backspace' && key !== 'Tab' && key !== 'ArrowLeft' && key !== 'ArrowRight') {
      event.preventDefault();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
